/**
 * データベース・認証エラークラス
 * 日本語エラーメッセージとユーザーガイダンス
 */

/**
 * エラーコード定義
 */
export enum ErrorCode {
  // 認証関連
  INVALID_CREDENTIALS = "AUTH_INVALID_CREDENTIALS",
  USER_NOT_FOUND = "AUTH_USER_NOT_FOUND",
  EMAIL_NOT_VERIFIED = "AUTH_EMAIL_NOT_VERIFIED",
  ACCOUNT_LOCKED = "AUTH_ACCOUNT_LOCKED",
  SESSION_EXPIRED = "AUTH_SESSION_EXPIRED",

  // OTP関連
  OTP_EXPIRED = "OTP_EXPIRED",
  OTP_INVALID = "OTP_INVALID",
  OTP_TOO_MANY_ATTEMPTS = "OTP_TOO_MANY_ATTEMPTS",
  OTP_SEND_FAILED = "OTP_SEND_FAILED",
  OTP_RATE_LIMITED = "OTP_RATE_LIMITED",

  // OAuth関連
  OAUTH_FAILED = "OAUTH_FAILED",
  OAUTH_CANCELLED = "OAUTH_CANCELLED",
  OAUTH_ACCOUNT_LINKING_ERROR = "OAUTH_ACCOUNT_LINKING_ERROR",

  // 権限関連
  INSUFFICIENT_PERMISSIONS = "AUTH_INSUFFICIENT_PERMISSIONS",
  ADMIN_REQUIRED = "AUTH_ADMIN_REQUIRED",
  MANAGER_REQUIRED = "AUTH_MANAGER_REQUIRED",
  STORE_ACCESS_DENIED = "AUTH_STORE_ACCESS_DENIED",
  HOSTESS_ROLE_REQUIRED = "AUTH_HOSTESS_ROLE_REQUIRED",

  // データベース関連
  DATABASE_ERROR = "DATABASE_ERROR",
  RLS_ERROR = "RLS_ERROR",
  UNIQUE_VIOLATION = "DATABASE_UNIQUE_VIOLATION",
  FOREIGN_KEY_VIOLATION = "DATABASE_FOREIGN_KEY_VIOLATION",
  NOT_NULL_VIOLATION = "DATABASE_NOT_NULL_VIOLATION",

  // ネットワーク関連
  NETWORK_ERROR = "NETWORK_ERROR",
  SERVER_ERROR = "SERVER_ERROR",
  SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",

  // バリデーション関連
  INVALID_EMAIL = "VALIDATION_INVALID_EMAIL",
  INVALID_PASSWORD = "VALIDATION_INVALID_PASSWORD",
  INVALID_TOKEN = "VALIDATION_INVALID_TOKEN",
  INVALID_INPUT = "VALIDATION_INVALID_INPUT",

  // その他
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  RATE_LIMITED = "RATE_LIMITED",
  MAINTENANCE_MODE = "MAINTENANCE_MODE",
}

/**
 * エラーメッセージとガイダンスのマッピング
 */
const ERROR_MESSAGES: Record<ErrorCode, {message: string; guidance: string; recoverable: boolean}> = {
  // 認証関連
  [ErrorCode.INVALID_CREDENTIALS]: {
    message: "メールアドレスまたはパスワードが正しくありません",
    guidance: "入力内容をご確認の上、再度お試しください。パスワードを忘れた場合は、メール認証をご利用ください。",
    recoverable: true,
  },
  [ErrorCode.USER_NOT_FOUND]: {
    message: "ユーザーが見つかりません",
    guidance: "メールアドレスをご確認ください。新規登録がまだの場合は、管理者にお問い合わせください。",
    recoverable: true,
  },
  [ErrorCode.EMAIL_NOT_VERIFIED]: {
    message: "メールアドレスの確認が完了していません",
    guidance: "登録時に送信された確認メールをご確認ください。メールが届いていない場合は、再送信をお試しください。",
    recoverable: true,
  },
  [ErrorCode.ACCOUNT_LOCKED]: {
    message: "アカウントがロックされています",
    guidance: "セキュリティ上の理由によりアカウントがロックされました。管理者にお問い合わせください。",
    recoverable: false,
  },
  [ErrorCode.SESSION_EXPIRED]: {
    message: "セッションの有効期限が切れました",
    guidance: "セキュリティのため、再度ログインしてください。",
    recoverable: true,
  },

  // OTP関連
  [ErrorCode.OTP_EXPIRED]: {
    message: "認証コードの有効期限が切れました",
    guidance: "新しい認証コードを再送信してください。",
    recoverable: true,
  },
  [ErrorCode.OTP_INVALID]: {
    message: "認証コードが正しくありません",
    guidance: "6桁の数字を正確に入力してください。",
    recoverable: true,
  },
  [ErrorCode.OTP_TOO_MANY_ATTEMPTS]: {
    message: "認証コードの入力回数が上限に達しました",
    guidance: "セキュリティのため、しばらく待ってから新しいコードを要求してください。",
    recoverable: false,
  },
  [ErrorCode.OTP_SEND_FAILED]: {
    message: "認証コードの送信に失敗しました",
    guidance: "メールアドレスが正しいことを確認し、再度お試しください。問題が続く場合は管理者にお問い合わせください。",
    recoverable: true,
  },
  [ErrorCode.OTP_RATE_LIMITED]: {
    message: "認証コードの送信回数が上限に達しました",
    guidance: "15分後に再度お試しください。",
    recoverable: false,
  },

  // OAuth関連
  [ErrorCode.OAUTH_FAILED]: {
    message: "Google認証に失敗しました",
    guidance: "もう一度お試しいただくか、メール認証をご利用ください。",
    recoverable: true,
  },
  [ErrorCode.OAUTH_CANCELLED]: {
    message: "Google認証がキャンセルされました",
    guidance: "認証を続けるには、もう一度Googleログインボタンをクリックしてください。",
    recoverable: true,
  },
  [ErrorCode.OAUTH_ACCOUNT_LINKING_ERROR]: {
    message: "アカウントの連携に失敗しました",
    guidance: "このGoogleアカウントは別のユーザーと連携済みです。別のアカウントでお試しください。",
    recoverable: false,
  },

  // 権限関連
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: {
    message: "このページへのアクセス権限がありません",
    guidance: "アクセス権限が必要な場合は、管理者にお問い合わせください。",
    recoverable: false,
  },
  [ErrorCode.ADMIN_REQUIRED]: {
    message: "管理者権限が必要です",
    guidance: "このページは管理者のみアクセス可能です。権限の付与については、本部にお問い合わせください。",
    recoverable: false,
  },
  [ErrorCode.MANAGER_REQUIRED]: {
    message: "マネージャー権限が必要です",
    guidance: "このページはマネージャー以上の権限が必要です。",
    recoverable: false,
  },
  [ErrorCode.STORE_ACCESS_DENIED]: {
    message: "この店舗へのアクセス権限がありません",
    guidance: "所属店舗の管理者にアクセス権限の付与を依頼してください。",
    recoverable: false,
  },
  [ErrorCode.HOSTESS_ROLE_REQUIRED]: {
    message: "ホステスロールが必要です",
    guidance: "このページはホステスのみアクセス可能です。",
    recoverable: false,
  },

  // データベース関連
  [ErrorCode.DATABASE_ERROR]: {
    message: "データベースエラーが発生しました",
    guidance: "再度お試しいただくか、問題が続く場合はサポートにお問い合わせください。",
    recoverable: true,
  },
  [ErrorCode.RLS_ERROR]: {
    message: "データへのアクセス権限がありません",
    guidance: "必要な権限がないか、データが存在しない可能性があります。",
    recoverable: false,
  },
  [ErrorCode.UNIQUE_VIOLATION]: {
    message: "既に登録されているデータです",
    guidance: "別の値を入力してください。",
    recoverable: true,
  },
  [ErrorCode.FOREIGN_KEY_VIOLATION]: {
    message: "関連するデータが存在しません",
    guidance: "参照先のデータを確認してください。",
    recoverable: true,
  },
  [ErrorCode.NOT_NULL_VIOLATION]: {
    message: "必須項目が入力されていません",
    guidance: "全ての必須項目を入力してください。",
    recoverable: true,
  },

  // ネットワーク関連
  [ErrorCode.NETWORK_ERROR]: {
    message: "ネットワークエラーが発生しました",
    guidance: "インターネット接続を確認し、再度お試しください。",
    recoverable: true,
  },
  [ErrorCode.SERVER_ERROR]: {
    message: "サーバーエラーが発生しました",
    guidance: "しばらく待ってから再度お試しください。問題が続く場合は、サポートにお問い合わせください。",
    recoverable: true,
  },
  [ErrorCode.SERVICE_UNAVAILABLE]: {
    message: "サービスが一時的に利用できません",
    guidance: "メンテナンス中の可能性があります。しばらく待ってから再度お試しください。",
    recoverable: true,
  },

  // バリデーション関連
  [ErrorCode.INVALID_EMAIL]: {
    message: "メールアドレスの形式が正しくありません",
    guidance: "有効なメールアドレスを入力してください（例：user@example.com）",
    recoverable: true,
  },
  [ErrorCode.INVALID_PASSWORD]: {
    message: "パスワードが要件を満たしていません",
    guidance: "パスワードは8文字以上で、大文字・小文字・数字を含む必要があります。",
    recoverable: true,
  },
  [ErrorCode.INVALID_TOKEN]: {
    message: "無効な認証トークンです",
    guidance: "セッションが無効です。再度ログインしてください。",
    recoverable: true,
  },
  [ErrorCode.INVALID_INPUT]: {
    message: "入力内容に誤りがあります",
    guidance: "入力内容を確認し、正しい形式で入力してください。",
    recoverable: true,
  },

  // その他
  [ErrorCode.UNKNOWN_ERROR]: {
    message: "予期しないエラーが発生しました",
    guidance: "再度お試しいただくか、問題が続く場合はサポートにお問い合わせください。",
    recoverable: true,
  },
  [ErrorCode.RATE_LIMITED]: {
    message: "リクエスト回数が上限に達しました",
    guidance: "しばらく待ってから再度お試しください。",
    recoverable: false,
  },
  [ErrorCode.MAINTENANCE_MODE]: {
    message: "システムメンテナンス中です",
    guidance: "メンテナンス終了までお待ちください。",
    recoverable: false,
  },
};

/**
 * ベースエラークラス
 */
export class DatabaseError extends Error {
  public readonly code: ErrorCode;
  public readonly guidance: string;
  public readonly recoverable: boolean;
  public readonly timestamp: Date;
  public readonly context?: Record<string, any>;

  constructor(code: ErrorCode, context?: Record<string, any>, customMessage?: string) {
    const errorInfo = ERROR_MESSAGES[code] || ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR];
    super(customMessage || errorInfo.message);

    this.name = "DatabaseError";
    this.code = code;
    this.guidance = errorInfo.guidance;
    this.recoverable = errorInfo.recoverable;
    this.timestamp = new Date();
    this.context = context;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, DatabaseError);
    }
  }

  getDetails(): {
    code: string;
    message: string;
    guidance: string;
    recoverable: boolean;
    timestamp: string;
    context?: Record<string, any>;
  } {
    return {
      code: this.code,
      message: this.message,
      guidance: this.guidance,
      recoverable: this.recoverable,
      timestamp: this.timestamp.toISOString(),
      context: this.context,
    };
  }

  getUserMessage(): string {
    return `${this.message}\n${this.guidance}`;
  }

  toJSON() {
    return this.getDetails();
  }
}

/**
 * 認証エラー専用クラス
 */
export class AuthError extends DatabaseError {
  constructor(code: ErrorCode, context?: Record<string, any>, customMessage?: string) {
    super(code, context, customMessage);
    this.name = "AuthError";
  }
}

/**
 * RLSエラー専用クラス
 */
export class RLSError extends DatabaseError {
  constructor(message?: string, context?: Record<string, any>) {
    super(ErrorCode.RLS_ERROR, context, message);
    this.name = "RLSError";
  }
}

/**
 * バリデーションエラー専用クラス
 */
export class ValidationError extends DatabaseError {
  public readonly field?: string;

  constructor(code: ErrorCode, field?: string, context?: Record<string, any>) {
    super(code, context);
    this.name = "ValidationError";
    this.field = field;
  }

  override getDetails() {
    return {
      ...super.getDetails(),
      field: this.field,
    };
  }
}

/**
 * エラーハンドリングユーティリティ
 */
export class ErrorHandler {
  /**
   * Supabaseエラーをカスタムエラーに変換
   */
  static fromSupabaseError(error: any): DatabaseError {
    const message = error.message?.toLowerCase() || "";
    const code = error.code || "";

    // PostgreSQLエラーコード
    switch (error.code) {
      case "23505":
        return new DatabaseError(ErrorCode.UNIQUE_VIOLATION, {originalError: error.message});
      case "23503":
        return new DatabaseError(ErrorCode.FOREIGN_KEY_VIOLATION, {originalError: error.message});
      case "23502":
        return new DatabaseError(ErrorCode.NOT_NULL_VIOLATION, {originalError: error.message});
      case "42501":
        return new RLSError(error.message, {originalError: error.message});
      case "PGRST301":
        return new AuthError(ErrorCode.SESSION_EXPIRED);
    }

    // 認証エラー
    if (message.includes("invalid login credentials")) {
      return new AuthError(ErrorCode.INVALID_CREDENTIALS);
    }
    if (message.includes("email not confirmed")) {
      return new AuthError(ErrorCode.EMAIL_NOT_VERIFIED);
    }
    if (message.includes("user not found")) {
      return new AuthError(ErrorCode.USER_NOT_FOUND);
    }
    if (message.includes("refresh token") || message.includes("jwt expired")) {
      return new AuthError(ErrorCode.SESSION_EXPIRED);
    }
    if (message.includes("rate limit")) {
      return new DatabaseError(ErrorCode.RATE_LIMITED);
    }

    // RLSエラー
    if (message.includes("row level security")) {
      return new RLSError(error.message, {originalError: error.message});
    }

    // OAuth
    if (code === "invalid_grant") {
      return new AuthError(ErrorCode.OAUTH_FAILED);
    }
    if (code === "access_denied") {
      return new AuthError(ErrorCode.OAUTH_CANCELLED);
    }

    return new DatabaseError(ErrorCode.UNKNOWN_ERROR, {
      originalError: error.message,
      errorCode: error.code,
    });
  }

  /**
   * HTTPステータスコードからエラーを生成
   */
  static fromHttpStatus(status: number, message?: string): DatabaseError {
    switch (status) {
      case 401:
        return new AuthError(ErrorCode.INVALID_CREDENTIALS);
      case 403:
        return new AuthError(ErrorCode.INSUFFICIENT_PERMISSIONS);
      case 404:
        return new AuthError(ErrorCode.USER_NOT_FOUND);
      case 429:
        return new DatabaseError(ErrorCode.RATE_LIMITED);
      case 500:
      case 502:
      case 503:
        return new DatabaseError(ErrorCode.SERVER_ERROR);
      case 504:
        return new DatabaseError(ErrorCode.SERVICE_UNAVAILABLE);
      default:
        return new DatabaseError(ErrorCode.UNKNOWN_ERROR, {
          httpStatus: status,
          message,
        });
    }
  }

  /**
   * エラーをログ用に整形
   */
  static formatForLogging(error: DatabaseError): string {
    const details = error.getDetails();
    return JSON.stringify(
      {
        ...details,
        stack: error.stack,
      },
      null,
      2,
    );
  }

  /**
   * エラーの重要度を判定
   */
  static getSeverity(error: DatabaseError): "low" | "medium" | "high" | "critical" {
    switch (error.code) {
      case ErrorCode.ACCOUNT_LOCKED:
      case ErrorCode.OTP_TOO_MANY_ATTEMPTS:
        return "critical";
      case ErrorCode.INSUFFICIENT_PERMISSIONS:
      case ErrorCode.ADMIN_REQUIRED:
      case ErrorCode.MANAGER_REQUIRED:
      case ErrorCode.RLS_ERROR:
        return "high";
      case ErrorCode.SESSION_EXPIRED:
      case ErrorCode.OTP_EXPIRED:
      case ErrorCode.DATABASE_ERROR:
        return "medium";
      default:
        return "low";
    }
  }
}


/** SQLSTATE/DETAIL をアプリまで保持する顧客参照ガードのエラー。 */
export class CustomerReferenceError extends Error {
  readonly code: "SK041" | "SK042";
  readonly table?: string;
  readonly column?: string;
  readonly customerId?: string;
  readonly mergedInto?: string;
  handled = false;

  private constructor(code: "SK041" | "SK042", message: string, detail: Record<string, unknown>) {
    super(message);
    this.name = "CustomerReferenceError";
    this.code = code;
    for (const key of ["table", "column", "customerId", "mergedInto"] as const) {
      this[key] = typeof detail[key] === "string" ? detail[key] : undefined;
    }
  }

  static fromPostgrestError(error: {code?: string; message?: string; details?: string | null}): CustomerReferenceError | undefined {
    if (error.code !== "SK041" && error.code !== "SK042") return undefined;
    let detail: Record<string, unknown> = {};
    try {
      const parsed: unknown = JSON.parse(error.details ?? "{}");
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) detail = parsed as Record<string, unknown>;
    } catch { /* DETAIL が壊れていても SQLSTATE は保持する */ }
    return new CustomerReferenceError(error.code, error.message ?? "顧客情報を更新してください", detail);
  }
}
